// New Items — the selling team's launchpad. Everything recently added to the
// catalog with what matters for getting it listed on marketplaces: per-item
// listing-readiness (UPC / photo / copy / dims), live + incoming stock with PO
// ETAs, full pricing, whether anyone has started selling it yet, and a detail
// drawer that is a complete copy-paste listing kit (identifiers, copy blocks,
// dimensions, image URLs).
//
// Backend: NewItemsController (api/NewItems/*) on the ClickHouse warehouse.
// Data honesty notes (profiled): most brand-new items have NO photo yet (they
// arrive later in the item lifecycle) and ~53% of incoming items carry a PO ETA.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton, TextField, MenuItem,
  ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, CircularProgress,
  Skeleton, Alert, InputAdornment, Select, Drawer, Divider, Avatar,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdNewReleases, MdInventory2, MdLocalShipping, MdChecklist, MdPhotoCamera,
  MdStorefront, MdRefresh, MdFileDownload, MdSearch, MdFilterListOff,
  MdContentCopy, MdClose, MdGridView, MdTableChart, MdOpenInNew,
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS, apiCall } from '../config/api';

const ACCENT = '#0072B2';
const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const money = (n) => (Number(n) > 0 ? usd.format(Number(n)) : '—');
const count = (n) => num.format(Number(n) || 0);
// the catalog uses a NUL byte as an "empty" sentinel — scrub it everywhere
const clean = (v) => (v == null ? '' : String(v)).replace(/\u0000/g, '').trim();

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const WINDOWS = [
  { key: '7d', label: '7D', days: 7 }, { key: '14d', label: '14D', days: 14 },
  { key: '30d', label: '30D', days: 30 }, { key: '60d', label: '60D', days: 60 },
  { key: '90d', label: '90D', days: 90 }, { key: '180d', label: '180D', days: 180 },
];
const PAGE = 60;

// availability badge for one item — overdue ETAs and next-year ETAs are called out
const etaText = (eta) => {
  const d = new Date(eta + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return { txt: '', overdue: false };
  const now = new Date();
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (d < today0) return { txt: `overdue ${eta.slice(5)}`, overdue: true };
  return { txt: d.getFullYear() === now.getFullYear() ? `Arriving ${eta.slice(5)}` : `Arriving ${eta}`, overdue: false };
};
const availability = (r) => {
  const atsq = Number(r.atsq) || 0;
  const futq = Number(r.futq) || 0;
  const eta = clean(r.poEta);
  if (atsq > 0) return { label: `In stock · ${count(atsq)}`, color: '#2e7d32', kind: 'instock' };
  if (futq > 0) {
    const e = eta ? etaText(eta) : { txt: '', overdue: false };
    if (e.overdue) return { label: `ETA ${e.txt} · ${count(futq)}`, color: '#c62828', kind: 'incoming' };
    return { label: e.txt ? `${e.txt} · ${count(futq)}` : `Incoming · ${count(futq)}`, color: '#b26a00', kind: 'incoming' };
  }
  return { label: 'No supply yet', color: '#888', kind: 'none' };
};

const ReadyDot = ({ ok, label }) => (
  <MuiTooltip title={`${label}: ${ok ? 'ready' : 'missing'}`}>
    <Chip size="small" label={label}
      sx={{ height: 18, fontSize: 9.5, fontWeight: 700, borderRadius: 1,
        color: ok ? '#2e7d32' : '#c62828',
        bgcolor: ok ? 'rgba(46,125,50,0.12)' : 'rgba(198,40,40,0.10)' }} />
  </MuiTooltip>
);

function KpiTile({ icon: Icon, label, value, hint, color = ACCENT }) {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: color + '1a', color, flexShrink: 0 }}>
          <Icon size={17} />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: .3, textTransform: 'uppercase', fontSize: 10.5 }} noWrap>{label}</Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15, mt: 0.5 }} noWrap>{value}</Typography>
      {hint && <Typography variant="caption" sx={{ color: 'text.disabled' }} noWrap>{hint}</Typography>}
    </Paper>
  );
}

// copyable text block for the listing kit
function CopyBlock({ title, text }) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const value = clean(text);
  if (!value) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>{title}</Typography>
        <Button size="small" startIcon={<MdContentCopy size={12} />}
          onClick={() => { navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); }).catch(() => {}); }}
          sx={{ textTransform: 'none', fontSize: 11, minWidth: 0, py: 0, color: copied ? 'success.main' : 'primary.main' }}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Stack>
      <Paper elevation={0} sx={{ p: 1, borderRadius: 1.5, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.action.hover }}>
        <Typography sx={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 130, overflowY: 'auto' }}>{value}</Typography>
      </Paper>
    </Box>
  );
}

const AttrRow = ({ k, v }) => {
  const value = clean(v);
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.35, borderBottom: '1px dashed', borderColor: 'divider' }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>{k}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  );
};

// =============================================================================
export default function NewItems() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const [filters, setFilters] = useState(() => ({
    preset: '30d', from: iso(addDays(new Date(), -30)), to: iso(new Date()),
    type: '', collection: '', q: '', stock: 'all', sort: 'created',
  }));
  const [collText, setCollText] = useState('');
  const [qText, setQText] = useState('');

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('gallery');
  const [shown, setShown] = useState(PAGE);
  const [detailItem, setDetailItem] = useState(null);   // ItemID being viewed
  const [detail, setDetail] = useState(null);           // fetched payload
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [broken, setBroken] = useState(() => new Set()); // image URLs that failed to load
  const abortRef = useRef(null);

  const qs = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    p.set('from', filters.from); p.set('to', filters.to);
    if (filters.type) p.set('type', filters.type);
    if (filters.collection) p.set('collection', filters.collection);
    if (filters.q) p.set('q', filters.q);
    if (filters.stock !== 'all') p.set('stock', filters.stock);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [filters]);

  // summary + list
  const loadAll = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true); setListLoading(true); setError('');
    const settled = await Promise.allSettled([
      apiCall(`${API_ENDPOINTS.NEW_ITEMS_SUMMARY}?${qs()}`, { signal: ac.signal }),
      apiCall(`${API_ENDPOINTS.NEW_ITEMS_LIST}?${qs({ sort: filters.sort, limit: 5000 })}`, { signal: ac.signal }),
    ]);
    if (abortRef.current !== ac) return;
    const [s, l] = settled;
    if (s.status === 'fulfilled' && s.value) setSummary(s.value);
    if (l.status === 'fulfilled' && l.value) { setRows(l.value.data || []); setTruncated(!!l.value.truncated); }
    const failed = settled.filter((x) => x.status === 'rejected' && x.reason?.name !== 'AbortError');
    if (failed.length) setError(failed[0].reason?.message || 'Failed to load');
    setLoading(false); setListLoading(false); setShown(PAGE);
  }, [qs, filters.sort]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // debounce text inputs
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.collection === collText.trim() ? f : { ...f, collection: collText.trim() })), 450);
    return () => clearTimeout(t);
  }, [collText]);
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === qText.trim() ? f : { ...f, q: qText.trim() })), 450);
    return () => clearTimeout(t);
  }, [qText]);

  // detail drawer fetch
  const [detailTry, setDetailTry] = useState(0);
  useEffect(() => {
    if (!detailItem) { setDetail(null); setDetailError(''); return undefined; }
    let live = true;
    setDetailLoading(true); setDetail(null); setDetailError('');
    apiCall(`${API_ENDPOINTS.NEW_ITEMS_DETAIL}?item=${encodeURIComponent(detailItem)}`)
      .then((r) => { if (live) { setDetail(r); setDetailLoading(false); } })
      .catch((e) => { if (live) { setDetailError(e.message || 'Failed to load item'); setDetailLoading(false); } });
    return () => { live = false; };
  }, [detailItem, detailTry]);

  const applyWindow = (key) => {
    const w = WINDOWS.find((x) => x.key === key);
    setFilters((f) => ({ ...f, preset: key, from: iso(addDays(new Date(), -(w?.days ?? 30))), to: iso(new Date()) }));
  };
  const setField = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => {
    setCollText(''); setQText('');
    setFilters({ preset: '30d', from: iso(addDays(new Date(), -30)), to: iso(new Date()), type: '', collection: '', q: '', stock: 'all', sort: 'created' });
  };

  const kpi = summary?.kpi || {};
  const types = summary?.types || [];
  const newCollections = summary?.newCollections || [];
  const visible = rows.slice(0, shown);

  // ---- export: the marketplace listing sheet ---------------------------------
  const exportExcel = () => {
    const head = ['SKU', 'Created', 'Collection', 'Product Type', 'Color', 'Size', 'Style', 'UPC',
      'MAP', 'Wholesale', 'MSRP', 'In Stock', 'Incoming', 'PO ETA', 'Already Sold (units)',
      'Has Photo', 'Has Copy', 'Has Dims', 'Description', 'Image URL'];
    const body = rows.map((r) => [
      clean(r.item), clean(r.created), clean(r.collection), clean(r.ptype), clean(r.color), clean(r.size), clean(r.style), clean(r.upc),
      Number(r.map) || 0, Number(r.wsp) || 0, Number(r.msrp) || 0,
      Number(r.atsq) || 0, Number(r.futq) || 0, clean(r.poEta), Number(r.soldUnits) || 0,
      Number(r.hasImage) ? 'YES' : 'NO', Number(r.hasCopy) ? 'YES' : 'NO', Number(r.hasDims) ? 'YES' : 'NO',
      clean(r.description), clean(r.thumb) || clean(r.imgLink),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...body]), 'New Items');
    XLSX.writeFile(wb, `New_Items_${filters.from}_to_${filters.to}.xlsx`);
  };

  const D = detail?.item || {};
  const bucketImages = detail?.bucketImages || [];
  const dSales = detail?.sales || {};
  const detailImages = useMemo(() => {
    const it = detail?.item || {};
    const bImgs = detail?.bucketImages || [];
    const urls = [];
    bImgs.forEach((b) => { const u = clean(b.url_large) || clean(b.url_original); if (u) urls.push(u); });
    for (let i = 1; i <= 12; i++) { const u = clean(it[`Image_Link_${i}`]); if (u && u.startsWith('http') && !urls.includes(u)) urls.push(u); }
    return urls;
  }, [detail]);

  const [heroImg, setHeroImg] = useState('');
  useEffect(() => { setHeroImg(detailImages[0] || ''); }, [detailImages]);

  // =============================================================================
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MdNewReleases size={26} color={ACCENT} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>New Items</Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Recently created products with listing readiness, stock, ETAs and pricing — get them selling
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<MdFileDownload />} onClick={exportExcel} disabled={listLoading || rows.length === 0}>Export listing sheet</Button>
          <Button size="small" variant="contained" startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <MdRefresh />} onClick={loadAll} disabled={loading}>Refresh</Button>
        </Stack>
      </Stack>

      {/* filter bar */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
          <ToggleButtonGroup size="small" exclusive value={filters.preset} onChange={(e, v) => v && applyWindow(v)}>
            {WINDOWS.map((w) => <ToggleButton key={w.key} value={w.key} sx={{ px: 1.1, fontWeight: 700 }}>{w.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <TextField size="small" select label="Product type" value={filters.type} onChange={(e) => setField('type', e.target.value)} sx={{ width: 190 }}>
            <MenuItem value="">All types</MenuItem>
            {types.map((t) => <MenuItem key={t.ptype} value={t.ptype}>{clean(t.ptype)} ({count(t.n)})</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Availability" value={filters.stock} onChange={(e) => setField('stock', e.target.value)} sx={{ width: 170 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="instock">In stock now</MenuItem>
            <MenuItem value="incoming">Arriving (on PO)</MenuItem>
            <MenuItem value="none">No supply yet</MenuItem>
          </TextField>
          <TextField size="small" select label="Sort" value={filters.sort} onChange={(e) => setField('sort', e.target.value)} sx={{ width: 160 }}>
            <MenuItem value="created">Newest first</MenuItem>
            <MenuItem value="stock">Most in stock</MenuItem>
            <MenuItem value="incoming">Most incoming</MenuItem>
            <MenuItem value="price">Highest MAP</MenuItem>
            <MenuItem value="collection">Collection A→Z</MenuItem>
          </TextField>
          <TextField size="small" placeholder="Collection…" value={collText} onChange={(e) => setCollText(e.target.value)} sx={{ width: 160 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
          <TextField size="small" placeholder="Search SKU / color / design…" value={qText} onChange={(e) => setQText(e.target.value)} sx={{ width: 210 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
          {(filters.type || filters.collection || filters.q || filters.stock !== 'all') && (
            <MuiTooltip title="Clear all filters">
              <IconButton size="small" onClick={clearFilters}><MdFilterListOff /></IconButton>
            </MuiTooltip>
          )}
          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)}>
            <ToggleButton value="gallery" sx={{ px: 1.2, textTransform: 'none' }}><MdGridView size={15} style={{ marginRight: 4 }} /> Gallery</ToggleButton>
            <ToggleButton value="table" sx={{ px: 1.2, textTransform: 'none' }}><MdTableChart size={15} style={{ marginRight: 4 }} /> Table</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPI row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', lg: 'repeat(6,1fr)' }, gap: 1.5, mb: 2 }}>
        {loading && !summary
          ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rounded" height={98} sx={{ borderRadius: 3 }} />)
          : (<>
            <KpiTile icon={MdNewReleases} label="New Items" value={count(kpi.newItems)} hint={`${filters.from} → ${filters.to}`} />
            <KpiTile icon={MdInventory2} label="In Stock Now" value={count(kpi.inStock)} hint={`${count(kpi.unitsOnHand)} units — sell today`} color="#2e7d32" />
            <KpiTile icon={MdLocalShipping} label="Arriving" value={count(kpi.incoming)} hint={`${count(kpi.unitsIncoming)} units on POs — prep listings`} color="#b26a00" />
            <KpiTile icon={MdChecklist} label="Listing-Ready" value={count(kpi.listingReady)} hint="UPC + photo + copy + dims" color="#009E73" />
            <KpiTile icon={MdPhotoCamera} label="With Photos" value={count(kpi.withImage)} hint="photos arrive after creation" color="#CC79A7" />
            <KpiTile icon={MdStorefront} label="Already Selling" value={count(kpi.alreadySelling)} hint={`${count((kpi.newItems || 0) - (kpi.alreadySelling || 0))} untapped`} color="#D55E00" />
          </>)}
      </Box>

      {/* newly launched collections */}
      {newCollections.length > 0 && (
        <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary', mb: 0.75 }}>
            Collections launched in this window
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {newCollections.map((c) => (
              <Chip key={c.collection} size="small" clickable
                label={`${clean(c.collection)} · ${count(c.items)} items`}
                onClick={() => { setCollText(clean(c.collection)); }}
                sx={{ fontWeight: 600, fontSize: 11 }} variant="outlined" color="primary" />
            ))}
          </Stack>
        </Paper>
      )}

      {truncated && <Alert severity="info" sx={{ mb: 2 }}>Showing the first 5,000 items — narrow the window or filters to see everything.</Alert>}

      {/* ---- gallery ---- */}
      {view === 'gallery' && (
        listLoading && rows.length === 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)', lg: 'repeat(5,1fr)' }, gap: 1.5 }}>
            {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} variant="rounded" height={280} sx={{ borderRadius: 3 }} />)}
          </Box>
        ) : visible.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.disabled' }}>No new items match these filters</Typography>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)', lg: 'repeat(5,1fr)' }, gap: 1.5 }}>
              {visible.map((r) => {
                const av = availability(r);
                const img = clean(r.thumb) || clean(r.imgLink);
                const sold = Number(r.soldUnits) || 0;
                return (
                  <Paper key={r.item} elevation={0} onClick={() => setDetailItem(r.item)}
                    sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', cursor: 'pointer',
                      transition: 'border-color .15s, transform .15s', '&:hover': { borderColor: ACCENT, transform: 'translateY(-2px)' } }}>
                    <Box sx={{ position: 'relative', aspectRatio: '1 / 1', bgcolor: dark ? '#1c1c2b' : '#f1f4f8', display: 'grid', placeItems: 'center' }}>
                      {img && !broken.has(img) ? (
                        <img src={img} alt={r.item} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={() => setBroken((prev) => new Set(prev).add(img))} />
                      ) : (
                        <Stack alignItems="center" spacing={0.5}>
                          <Avatar variant="rounded" sx={{ width: 54, height: 54, bgcolor: ACCENT + '22', color: ACCENT, fontWeight: 800, fontSize: 16 }}>
                            {clean(r.item).slice(0, 3)}
                          </Avatar>
                          <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>photo pending</Typography>
                        </Stack>
                      )}
                      {sold === 0 && (
                        <Chip label="NOT YET SOLD" size="small"
                          sx={{ position: 'absolute', top: 8, left: 8, height: 20, fontSize: 9.5, fontWeight: 800, bgcolor: '#D55E00', color: '#fff' }} />
                      )}
                      {sold > 0 && (
                        <Chip label={`Selling · ${count(sold)}`} size="small"
                          sx={{ position: 'absolute', top: 8, left: 8, height: 20, fontSize: 9.5, fontWeight: 800, bgcolor: '#2e7d32', color: '#fff' }} />
                      )}
                    </Box>
                    <Box sx={{ p: 1.25 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 13 }} noWrap>{clean(r.item)}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
                        {[clean(r.collection), clean(r.size)].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{clean(r.color) || ' '}</Typography>
                      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{money(r.map)}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>WSP {money(r.wsp)}</Typography>
                      </Stack>
                      <Chip label={av.label} size="small" sx={{ mt: 0.6, height: 20, fontSize: 10, fontWeight: 700, color: '#fff', bgcolor: av.color }} />
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                        <ReadyDot ok={!!Number(r.hasUpc)} label="UPC" />
                        <ReadyDot ok={!!Number(r.hasImage)} label="PHOTO" />
                        <ReadyDot ok={!!Number(r.hasCopy)} label="COPY" />
                        <ReadyDot ok={!!Number(r.hasDims)} label="DIMS" />
                      </Stack>
                      <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.5 }}>added {clean(r.created)}</Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
            {shown < rows.length && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button variant="outlined" onClick={() => setShown((n) => n + PAGE)}>
                  Show more ({count(rows.length - shown)} remaining)
                </Button>
              </Box>
            )}
          </>
        )
      )}

      {/* ---- table ---- */}
      {view === 'table' && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1050 }}>
              <TableHead>
                <TableRow>
                  {['SKU', 'Created', 'Collection', 'Type', 'Color', 'Size', 'MAP', 'WSP', 'Stock', 'Incoming', 'PO ETA', 'Ready', 'Sold'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', py: '9px' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {listLoading && rows.length === 0 ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={13}><Skeleton height={20} /></TableCell></TableRow>
                  ))
                ) : visible.length === 0 ? (
                  <TableRow><TableCell colSpan={13} sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>No new items match these filters</TableCell></TableRow>
                ) : (
                  visible.map((r) => {
                    const ready = ['hasUpc', 'hasImage', 'hasCopy', 'hasDims'].filter((k) => Number(r[k])).length;
                    return (
                      <TableRow key={r.item} hover onClick={() => setDetailItem(r.item)} sx={{ cursor: 'pointer', '&:nth-of-type(even)': { bgcolor: theme.palette.action.hover } }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12.5 }}>{clean(r.item)}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{clean(r.created)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{clean(r.collection)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{clean(r.ptype)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{clean(r.color)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{clean(r.size)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{money(r.map)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{money(r.wsp)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{count(r.atsq)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{count(r.futq)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{clean(r.poEta) || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700, color: ready === 4 ? '#2e7d32' : ready >= 2 ? '#b26a00' : '#c62828' }}>{ready}/4</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{count(r.soldUnits)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
          {shown < rows.length && (
            <Box sx={{ textAlign: 'center', py: 1.5 }}>
              <Button variant="outlined" size="small" onClick={() => setShown((n) => n + PAGE)}>Show more ({count(rows.length - shown)} remaining)</Button>
            </Box>
          )}
        </Paper>
      )}

      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 2, textAlign: 'center' }}>
        New = catalog CreatedDate in window · discontinued excluded · readiness = UPC / photo / description / package dims · "Arriving" = units on purchase orders (earliest PO ETA) · sold = invoiced units since creation window start
      </Typography>

      {/* ---- detail drawer: the listing kit ---- */}
      <Drawer anchor="right" open={!!detailItem} onClose={() => setDetailItem(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, p: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{detailItem}</Typography>
          <IconButton size="small" onClick={() => setDetailItem(null)}><MdClose /></IconButton>
        </Stack>
        {detailLoading && <Skeleton variant="rounded" height={380} sx={{ borderRadius: 2 }} />}
        {!detailLoading && !detail && detailError && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setDetailTry((t) => t + 1)}>Retry</Button>}>
            {detailError}
          </Alert>
        )}
        {!detailLoading && detail && (
          <Box sx={{ overflowY: 'auto' }}>
            {/* images */}
            {detailImages.length > 0 ? (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}`, aspectRatio: '1 / 1', display: 'grid', placeItems: 'center', bgcolor: dark ? '#1c1c2b' : '#f1f4f8' }}>
                  {heroImg && !broken.has(heroImg) ? (
                    <img src={heroImg} alt={detailItem} style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={() => setBroken((prev) => new Set(prev).add(heroImg))} />
                  ) : (
                    <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>image unavailable</Typography>
                  )}
                </Box>
                {detailImages.length > 1 && (
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, overflowX: 'auto', pb: 0.5 }}>
                    {detailImages.map((u) => (
                      <Box key={u} onClick={() => setHeroImg(u)}
                        sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: 1, overflow: 'hidden', cursor: 'pointer',
                          border: `2px solid ${u === heroImg ? ACCENT : theme.palette.divider}` }}>
                        <img src={u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    ))}
                  </Stack>
                )}
                <Button size="small" startIcon={<MdContentCopy size={13} />} sx={{ mt: 0.5, textTransform: 'none', fontSize: 11 }}
                  onClick={() => navigator.clipboard.writeText(detailImages.join('\n')).catch(() => {})}>
                  Copy all {detailImages.length} image URLs
                </Button>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mb: 1.5 }}>No photos available yet — photography usually lands after the item is received.</Alert>
            )}

            {/* identity + status */}
            <Paper elevation={0} sx={{ p: 1.25, mb: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <AttrRow k="UPC" v={D.UPC} />
              <AttrRow k="Alt UPC" v={D.ALTUPC} />
              <AttrRow k="Created" v={D.created} />
              <AttrRow k="First received" v={D.firstReceived} />
              <AttrRow k="Status" v={D.ActiveStatus} />
              <AttrRow k="Exclusive for" v={D.ExclusiveFor} />
              {clean(D.UPC) && (
                <Button size="small" startIcon={<MdContentCopy size={13} />} sx={{ mt: 0.5, textTransform: 'none', fontSize: 11 }}
                  onClick={() => navigator.clipboard.writeText(clean(D.UPC)).catch(() => {})}>Copy UPC</Button>
              )}
            </Paper>

            {/* stock + pricing */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
              <Paper elevation={0} sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 0.5 }}>Stock</Typography>
                <AttrRow k="Available (ATS)" v={count(D.ATSQ)} />
                <AttrRow k="Warehouse" v={count(D.WHSQ)} />
                <AttrRow k="Incoming (FUT)" v={count(D.FUTQ)} />
                <AttrRow k="Backordered" v={count(D.BO)} />
                <AttrRow k="Earliest PO ETA" v={D.EarliestPOETADate} />
                <AttrRow k="PO qty" v={Number(D.EarliestPOQty) > 0 ? count(D.EarliestPOQty) : ''} />
              </Paper>
              <Paper elevation={0} sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 0.5 }}>Pricing</Typography>
                <AttrRow k="MAP" v={money(D.Retail_MapPrice)} />
                <AttrRow k="MSRP" v={money(D.MSRPPrice)} />
                <AttrRow k="Wholesale" v={money(D.WholeSalePrice)} />
                <AttrRow k="Stocking dealer" v={money(D.StockingDealerPrice)} />
                <AttrRow k="Non-stocking" v={money(D.NonStockingDealerPrice)} />
                <AttrRow k="Designer" v={money(D.DesignerPrice)} />
              </Paper>
            </Box>

            {/* sales so far */}
            <Paper elevation={0} sx={{ p: 1.25, mb: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 0.5 }}>Sales so far</Typography>
              {Number(dSales.units) > 0 ? (
                <>
                  <AttrRow k="Units sold" v={count(dSales.units)} />
                  <AttrRow k="Revenue" v={money(dSales.revenue)} />
                  <AttrRow k="First sale" v={dSales.firstSale} />
                  <AttrRow k="Last sale" v={dSales.lastSale} />
                  <AttrRow k="Customers" v={count(dSales.customers)} />
                </>
              ) : (
                <Chip label="NOT YET SOLD — open opportunity" size="small" sx={{ fontWeight: 800, bgcolor: '#D55E00', color: '#fff' }} />
              )}
            </Paper>

            {/* copy blocks — the listing kit */}
            <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>Listing copy</Typography>
            <CopyBlock title="Title / Description" text={D.Description} />
            <CopyBlock title="Extended title" text={[D.Extended_Title, D.Extended_Title_1, D.Extended_Title_2, D.Extended_Title_3].map(clean).filter(Boolean).join('\n')} />
            <CopyBlock title="Bullet points" text={D.Bullet_Points} />
            <CopyBlock title="Amazon description" text={D.Amazon_Description} />
            <CopyBlock title="Marketing copy" text={[D.MarketingCopy1, D.MarketingCopy3, D.MarketingCopy4, D.MarketingCopy5].map(clean).filter(Boolean).join('\n\n')} />
            <CopyBlock title="Long description" text={[D.Desc1, D.Desc2, D.Desc3, D.Desc4].map(clean).filter(Boolean).join('\n')} />
            <CopyBlock title="Keywords" text={D.Keywords} />
            <CopyBlock title="Room types" text={D.Room_Types} />

            {/* attributes */}
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>Attributes</Typography>
            <Paper elevation={0} sx={{ p: 1.25, mb: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <AttrRow k="Collection" v={D.Collection} />
              <AttrRow k="Product type" v={D.ProductType} />
              <AttrRow k="Design" v={D.DesignName || D.Design} />
              <AttrRow k="Color" v={D.Color} />
              <AttrRow k="Color family" v={D.ColorFamily} />
              <AttrRow k="Size" v={D.Size} />
              <AttrRow k="Shape" v={D.Shape} />
              <AttrRow k="Style" v={D.Style} />
              <AttrRow k="Pattern" v={D.Pattern} />
              <AttrRow k="Weave" v={D.Weave} />
              <AttrRow k="Content" v={D.Content} />
              <AttrRow k="Pile content" v={D.PileContent} />
              <AttrRow k="Backing" v={D.Backing} />
              <AttrRow k="Pile height" v={D.RugHeight} />
              <AttrRow k="Origin" v={D.Origin} />
              <AttrRow k="Indoor/Outdoor" v={D.Indoor_Outdoor} />
              <AttrRow k="Machine washable" v={D.MachineWashable} />
              <AttrRow k="Non-slip" v={D.NonSlip} />
            </Paper>

            {/* shipping */}
            <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>Shipping & dimensions</Typography>
            <Paper elevation={0} sx={{ p: 1.25, mb: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <AttrRow k="Package L × W × H (in)" v={Number(D.PackageLength) > 0 ? `${D.PackageLength} × ${D.PackageWidth} × ${D.PackageHeight}` : ''} />
              <AttrRow k="Weight (lb)" v={Number(D.WeightLb) > 0 ? D.WeightLb : ''} />
              <AttrRow k="Dim weight" v={Number(D.Dimentional_Weight) > 0 ? D.Dimentional_Weight : ''} />
              <AttrRow k="Product dims" v={D.ProductDimensionsWxDxH} />
              <AttrRow k="Ship type" v={D.ShipType} />
              <AttrRow k="Multi-box" v={D.Multi_Box} />
              <AttrRow k="Box 1" v={clean(D.Box1_Item) ? `${clean(D.Box1_Item)} × ${clean(D.Box1_Qty)}` : ''} />
              <AttrRow k="Box 2" v={clean(D.Box2_Item) ? `${clean(D.Box2_Item)} × ${clean(D.Box2_Qty)}` : ''} />
              <AttrRow k="Box 3" v={clean(D.Box3_Item) ? `${clean(D.Box3_Item)} × ${clean(D.Box3_Qty)}` : ''} />
            </Paper>

            {(clean(D.Video_Link) || clean(D.Assembly_Instructions)) && (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {clean(D.Video_Link) && (
                  <Button size="small" variant="outlined" endIcon={<MdOpenInNew size={13} />} href={clean(D.Video_Link)} target="_blank" rel="noopener" sx={{ textTransform: 'none', fontSize: 11 }}>Video</Button>
                )}
                {clean(D.Assembly_Instructions) && (
                  <Button size="small" variant="outlined" endIcon={<MdOpenInNew size={13} />} href={clean(D.Assembly_Instructions)} target="_blank" rel="noopener" sx={{ textTransform: 'none', fontSize: 11 }}>Assembly PDF</Button>
                )}
              </Stack>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
